import {
    Box,
    Card,
    CardContent,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import { applyRoundOffLogic } from "common/src/other/RoundOffLogic.js";

const useStyles = makeStyles((theme) => ({
    card: {
        marginBottom: theme.spacing(3),
        borderRadius: 12,
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        border: "1px solid #e0e0e0",
    },
    cardContent: {
        padding: theme.spacing(3),
    },
    title: {
        fontSize: "1.25rem",
        fontWeight: "bold",
        color: "#333",
        marginBottom: theme.spacing(1),
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    description: {
        fontSize: "0.875rem",
        color: "#666",
        marginTop: theme.spacing(1),
        lineHeight: 1.5,
    },
    switchContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
    },
    switch: {
        marginLeft: theme.spacing(2),
    },
    formControl: {
        marginBottom: "16px",
        minWidth: "100%",
    },
    textField: {
        marginBottom: "16px",
    },
}));

const RoundOffConfig = ({
    data,
    setLocalData,
    handleTextChange,
    handleSwitchChange,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();

    // Check if trip amount round-off is enabled in current data
    const isRoundOffEnabled = !!(
        data &&
        data.trip_amount_roundoff &&
        data.trip_amount_roundoff.enabled
    );

    // Handle toggle switch for enabling/disabling roundoff
    const handleToggleRoundOff = () => {
        if (isRoundOffEnabled) {
            // Disable roundoff - set enabled to false
            setLocalData((prev) => ({
                ...prev,
                trip_amount_roundoff: {
                    ...data.trip_amount_roundoff,
                    enabled: false,
                },
            }));
        } else {
            // Enable roundoff - set enabled to true with default settings if needed
            const currentSettings = data.trip_amount_roundoff || {};
            setLocalData((prev) => ({
                ...prev,
                trip_amount_roundoff: {
                    adjustToAmount: 100,
                    roundBy: "last_two_digits",
                    thresholdAmount: 50,
                    ...currentSettings, // Keep existing settings if any
                    enabled: true, // Override enabled to true
                },
            }));
        }
    };

    // Handle roundoff setting changes
    const handleRoundOffChange = (field, value) => {
        setLocalData((prev) => ({
            ...prev,
            trip_amount_roundoff: {
                ...data.trip_amount_roundoff,
                enabled: true, // Ensure enabled is always true when changing settings
                [field]: value,
            },
        }));
    };

    return (
        <Grid item xs={12} md={12}>
            <Card className={classes.card}>
                <CardContent className={classes.cardContent}>
                    <div className={classes.switchContainer}>
                        <div>
                            <Typography className={classes.title}>
                                {t("trip_amount_roundoff") ||
                                    "Trip Amount Roundoff"}
                            </Typography>
                            <Typography className={classes.description}>
                                {t("trip_amount_roundoff_description") ||
                                    "Automatically round trip amounts based on configured rules. Useful for simplifying fare calculations and providing cleaner pricing."}
                            </Typography>
                        </div>
                        <Switch
                            className={classes.switch}
                            checked={isRoundOffEnabled}
                            onChange={handleToggleRoundOff}
                            color="primary"
                        />
                    </div>

                    {isRoundOffEnabled && (
                        <>
                            <Grid
                                container
                                spacing={3}
                                style={{ marginTop: 16 }}
                            >
                                <Grid item xs={12} sm={4}>
                                    <FormControl
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                    >
                                        <InputLabel>
                                            {t("round_by") || "Round By"}
                                        </InputLabel>
                                        <Select
                                            value={
                                                data.trip_amount_roundoff
                                                    ?.roundBy ||
                                                "last_two_digits"
                                            }
                                            onChange={(e) =>
                                                handleRoundOffChange(
                                                    "roundBy",
                                                    e.target.value
                                                )
                                            }
                                            label={t("round_by") || "Round By"}
                                        >
                                            <MenuItem value="last_digit">
                                                {t("last_digit") ||
                                                    "Last Digit"}
                                            </MenuItem>
                                            <MenuItem value="last_two_digits">
                                                {t("last_two_digits") ||
                                                    "Last Two Digits"}
                                            </MenuItem>
                                            <MenuItem value="last_three_digits">
                                                {t("last_three_digits") ||
                                                    "Last Three Digits"}
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label={
                                            t("adjust_to_amount") ||
                                            "Adjust To Amount"
                                        }
                                        type="number"
                                        value={
                                            data.trip_amount_roundoff
                                                ?.adjustToAmount || 100
                                        }
                                        onChange={(e) =>
                                            handleRoundOffChange(
                                                "adjustToAmount",
                                                parseFloat(e.target.value) ||
                                                    100
                                            )
                                        }
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        inputProps={{ min: 1, step: 1 }}
                                        helperText={
                                            t("roundoff_amount_help") ||
                                            "Amount to round to (e.g., 100 for rounding to nearest hundred)"
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label={
                                            t("threshold_amount") ||
                                            "Threshold Amount"
                                        }
                                        type="number"
                                        value={
                                            data.trip_amount_roundoff
                                                ?.thresholdAmount || 50
                                        }
                                        onChange={(e) =>
                                            handleRoundOffChange(
                                                "thresholdAmount",
                                                parseFloat(e.target.value) || 50
                                            )
                                        }
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        inputProps={{
                                            min: 0,
                                            max: 99,
                                            step: 1,
                                        }}
                                        helperText={
                                            t("threshold_help") ||
                                            "Threshold for rounding up (0-99)"
                                        }
                                    />
                                </Grid>
                            </Grid>

                            <Box mt={2}>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    <strong>
                                        {t("example") || "Example"}:
                                    </strong>
                                </Typography>
                                {(() => {
                                    const roundBy =
                                        data.trip_amount_roundoff?.roundBy ||
                                        "last_two_digits";
                                    const adjustToAmount =
                                        data.trip_amount_roundoff
                                            ?.adjustToAmount || 100;
                                    const thresholdAmount =
                                        data.trip_amount_roundoff
                                            ?.thresholdAmount || 50;

                                    // Create settings object for RoundOffLogic
                                    const settings = {
                                        roundBy,
                                        thresholdAmount,
                                        adjustToAmount,
                                        adjustmentToMount: adjustToAmount, // For compatibility
                                    };

                                    // Helper function to get digit description using translation
                                    const getDigitDescription = (roundBy) => {
                                        if (roundBy === "last_digit") {
                                            return (
                                                t("last_digit") || "Last Digit"
                                            );
                                        } else if (
                                            roundBy === "last_two_digits"
                                        ) {
                                            return (
                                                t("last_two_digits") ||
                                                "Last Two Digits"
                                            );
                                        } else {
                                            return (
                                                t("last_three_digits") ||
                                                "Last Three Digits"
                                            );
                                        }
                                    };

                                    // Calculate actual examples using RoundOffLogic - use different examples for 3 digits
                                    const example1 =
                                        roundBy === "last_three_digits"
                                            ? 12345.67
                                            : 4112.4;
                                    const example2 =
                                        roundBy === "last_three_digits"
                                            ? 15678.9
                                            : 4295.76;

                                    const result1 = applyRoundOffLogic(
                                        example1,
                                        settings
                                    );
                                    const result2 = applyRoundOffLogic(
                                        example2,
                                        settings
                                    );

                                    // Get the relevant digits for explanation
                                    const digits1 =
                                        roundBy === "last_digit"
                                            ? Math.floor(example1) % 10
                                            : roundBy === "last_two_digits"
                                            ? Math.floor(example1) % 100
                                            : Math.floor(example1) % 1000;
                                    const digits2 =
                                        roundBy === "last_digit"
                                            ? Math.floor(example2) % 10
                                            : roundBy === "last_two_digits"
                                            ? Math.floor(example2) % 100
                                            : Math.floor(example2) % 1000;

                                    return (
                                        <>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                • {example1} → {result1} (
                                                {getDigitDescription(
                                                    roundBy
                                                ).toLowerCase()}
                                                : {digits1}{" "}
                                                {digits1 >= thresholdAmount
                                                    ? "≥"
                                                    : "<"}{" "}
                                                {thresholdAmount})
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                • {example2} → {result2} (
                                                {getDigitDescription(
                                                    roundBy
                                                ).toLowerCase()}
                                                : {digits2}{" "}
                                                {digits2 >= thresholdAmount
                                                    ? "≥"
                                                    : "<"}{" "}
                                                {thresholdAmount})
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                style={{
                                                    fontSize: "0.75rem",
                                                    fontStyle: "italic",
                                                }}
                                            >
                                                {(() => {
                                                    const nearestValue =
                                                        roundBy === "last_digit"
                                                            ? "10"
                                                            : roundBy ===
                                                              "last_two_digits"
                                                            ? "100"
                                                            : "1000";
                                                    const logicText =
                                                        t(
                                                            "roundoff_logic_explanation"
                                                        ) ||
                                                        "Logic: Round DOWN to nearest {0}, then add {1} if threshold met";
                                                    return logicText
                                                        .replace(
                                                            "{0}",
                                                            nearestValue
                                                        )
                                                        .replace(
                                                            "{1}",
                                                            adjustToAmount
                                                        );
                                                })()}
                                            </Typography>
                                        </>
                                    );
                                })()}
                            </Box>
                        </>
                    )}
                </CardContent>
            </Card>
        </Grid>
    );
};

export default RoundOffConfig;
